/**
 * A timeline's tempo in words, for the pickers (2026-11-03, operator: "in timeline picker, i need the bpm of the
 * timeline", then "it's the timeline's and if the song is different then put the song bpm in brackets ()").
 *
 *   own tempo only, or its song agrees  -> "120"
 *   own tempo and a song at another     -> "120 (126)"
 *   no tempo of its own, a song         -> "126"
 *   neither (or the song not read yet)  -> ""
 *
 * The timeline's own is `Timeline.bpm` (what OPBridge plays it at with Live closed); the song's is its beat map's grid
 * tempo with the operator's half / double pick (`gridBpm`), as the editor's BPM readout shows it.
 */
import { createEffect } from 'solid-js';
import type { Timeline } from '@opbuttons/schema';
import { bridges, getConnection, primaryBridge } from '../opbridge/store';
import { gridBpm } from './testTrack.ts';
import { beatmaps, loadBeatmap } from './testTrackStore.ts';

/** One decimal at most, none when whole: 120, 125.9. */
export const bpmWord = (n: number): string => String(Math.round(n * 10) / 10);

export function ownBpm(t: Timeline): number | undefined {
  return typeof t.bpm === 'number' && Number.isFinite(t.bpm) && t.bpm > 0 ? t.bpm : undefined;
}
export function songBpm(t: Timeline): number | undefined {
  const id = t.testTrack?.id;
  const m = id ? beatmaps()[id] : undefined;
  return m && Number.isFinite(m.bpm) && m.bpm > 0 ? gridBpm(m) : undefined;
}

export function tempoText(t: Timeline): string {
  const own = ownBpm(t);
  const song = songBpm(t);
  if (own === undefined) return song === undefined ? '' : bpmWord(song);
  if (song === undefined || bpmWord(song) === bpmWord(own)) return bpmWord(own);
  return `${bpmWord(own)} (${bpmWord(song)})`;
}

/** The same in a sentence, for a title: "120 BPM, its song 126 BPM". */
export function tempoTitle(t: Timeline): string {
  const own = ownBpm(t);
  const song = songBpm(t);
  if (own === undefined) return song === undefined ? 'No tempo of its own and no song' : `Its song's tempo, ${bpmWord(song)} BPM`;
  if (song === undefined || bpmWord(song) === bpmWord(own)) return `${bpmWord(own)} BPM`;
  return `${bpmWord(own)} BPM, its song ${bpmWord(song)} BPM`;
}

/** Read the songs' tempos for the timelines a picker lists, once OPBridge is reachable. Call inside a component. */
export function followSongTempos(list: () => readonly Timeline[]): void {
  createEffect(() => {
    bridges();
    const p = primaryBridge();
    if (!p || getConnection(p.id)?.statusSignal()() !== 'connected') return;
    const have = beatmaps();
    for (const t of list()) { const id = t.testTrack?.id; if (id && !(id in have)) void loadBeatmap(id); }
  });
}
